// The /state entry exposes the filter scope without any UI imports, so pulling
// filter state into the app (and its tests) never evaluates kgf's components.

import { getFilterScope, type Scope } from '@statsbomb/kitbag-global-football/state';
import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { ConfigFilterAtomValues } from '@/types/userConfigs';

/**
 * Which kitbag-global-football filter scope this store's filter atoms resolve
 * to. Defaults to the page-level 'main' scope. A dashboard widget's
 * ScopeProvider overrides it with a per-widget id, so each widget's metric is
 * isolated while the page-level filters (which widgets read from shared,
 * unscoped param atoms) stay shared.
 */
export const filterScopeIdAtom = atom<Scope>('main');

/**
 * A subscription-backed snapshot of one scope's filter values, keyed by scope
 * id. Each family entry's onMount subscribes to its own scope, so the app's
 * derived-atom / query layer keeps composing with `get()`/`set()` unchanged
 * while resolving to whichever scope the current store is pointed at.
 */
const scopeSnapshotFamily = atomFamily((scopeId: Scope) => {
  const handle = getFilterScope(scopeId);
  const snapshot = atom(handle.get());
  snapshot.onMount = setSnapshot => {
    setSnapshot(handle.get());
    return handle.subscribe(setSnapshot);
  };
  return snapshot;
});

export const filtersAtom = atom(
  get => get(scopeSnapshotFamily(get(filterScopeIdAtom))),
  (get, set, next: ConfigFilterAtomValues | ((prev: ConfigFilterAtomValues) => ConfigFilterAtomValues)) => {
    const scopeId = get(filterScopeIdAtom);
    const resolved = typeof next === 'function' ? next(getFilterScope(scopeId).get()) : next;
    getFilterScope(scopeId).set(resolved);
    // Write through to the local snapshot so a read in the same store is
    // correct even before (or without) the mount-time subscription.
    set(scopeSnapshotFamily(scopeId), getFilterScope(scopeId).get());
  },
);

/**
 * A read-only projection of a single filter value off the scope mirror. Use it
 * where the reactive query layer needs an atom for one filter key (it can't
 * call useFilters), e.g. as an argument to objectFilterParamsFn / minMaxFilterAtom.
 */
export const filterValueAtom = <K extends keyof ConfigFilterAtomValues>(filterKey: K) =>
  atom(get => get(filtersAtom)[filterKey]);

/**
 * The options kitbag-global-football's data hooks (e.g. usePlayerFilterOptions)
 * fetch and feed into the scope — cycles, competition and season options. These
 * are page-level (fetched into 'main'); widgets don't fetch their own, so this
 * stays bound to 'main'.
 */
const mainScope = getFilterScope('main');
const optionsSnapshotAtom = atom(mainScope.getOptions());
optionsSnapshotAtom.onMount = setSnapshot => {
  setSnapshot(mainScope.getOptions());
  return mainScope.subscribeOptions(setSnapshot);
};

export const scopeFilterOptionsAtom = atom(get => get(optionsSnapshotAtom));
